<script setup lang="ts">
import { computed, useId } from 'vue'

interface Props {
  size?: number | string
}

const props = withDefaults(defineProps<Props>(), {
  size: 64,
})

const uid = useId()

const sizePx = computed(() => {
  if (typeof props.size === 'number')
    return `${props.size}px`
  return props.size.endsWith('px') || props.size.endsWith('rem') || props.size.endsWith('%')
    ? props.size
    : `${props.size}px`
})
</script>

<template>
  <div
    class="animated-logo-loader"
    :style="{ width: sizePx, height: sizePx }"
    role="status"
    aria-label="Загрузка..."
  >
    <svg
      viewBox="0 0 1094.4 1124"
      class="animated-logo-svg"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <!-- 1. Оригинальный градиент нижнего контура (строго 1-в-1 из LogoSmall.svg) -->
        <linearGradient
          :id="`logo-grad-1-${uid}`"
          gradientUnits="userSpaceOnUse"
          x1="945.0196"
          y1="894.1213"
          x2="90.0533"
          y2="390.5396"
          gradientTransform="matrix(1 0 0 1 0 88.9607)"
        >
          <stop offset="0" stop-color="#1D4D5B" />
          <stop offset="1" stop-color="#29A4AB" />
        </linearGradient>

        <!-- 2. Оригинальный градиент верхнего контура (строго 1-в-1 из LogoSmall.svg) -->
        <linearGradient
          :id="`logo-grad-2-${uid}`"
          gradientUnits="userSpaceOnUse"
          x1="802.5514"
          y1="636.761"
          x2="248.8859"
          y2="43.1758"
          gradientTransform="matrix(1 0 0 1 0 88.9607)"
        >
          <stop offset="0" stop-color="#215F6C" />
          <stop offset="1" stop-color="#25B4B7" />
        </linearGradient>

        <!-- 3. Фильтр неонового свечения для луча в щели -->
        <filter :id="`ecg-beam-glow-${uid}`" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <!-- БАЗОВЫЙ СЛОЙ ЛОГОТИПА: верхняя и нижняя половинки креста -->
      <g class="logo-base-layer">
        <path
          :fill="`url(#logo-grad-1-${uid})`"
          d="M524.6,289c2.3,34.3,9.5,70.2,15.6,104.1l25.8,145c16.1,95.9,28.5,194.2,48.2,289
          c22.6,29.5,52.4,11.8,59.5-13.4C702.5,711,737,608,764,505.6c21.3,59.9,48.4,97.8,117.6,96.2c46.1-1,94.1-0.4,140.3-0.5
          c24-0.2,48.4-0.2,72.4-0.8l0.1,71.8c0,40.4,3.3,83.2-27.1,114.9c-9.4,9.8-26,19.5-39.7,20.9c-8.2,2.9-38.1,2.2-48,2.1L874,809.6
          c-19-0.1-43.2-1.2-61.6,1.5c-17.7,2.6-23.4,7.5-33.2,21.1c-8.4,54.7,2.4,132.1-2,189.9c-7.5,100.1-62.8,102.6-144.7,100
          c-73.8-2.3-148.4,3.8-222.2,1.1c-90.5-5.6-91.9-78.3-92.3-147.3l0.4-94.1c0.7-81.4-19.9-72.5-92.7-71.8l-117.2,1.5
          c-30.9,0.2-56-4.3-78.9-27.7C-1.5,752,2,711.7,2.1,671l0.4-68.2l183.8-0.8c24.7-0.2,58.6-2,82.2-0.9c34,1.7,50.7,68,85.5,75.7
          c19.6,5.9,42.5-7.1,50.9-25.4c16.2-35.4,27.7-75.1,40.4-112C473.9,456.3,502,374.1,524.6,289z"
        />
        <path
          :fill="`url(#logo-grad-2-${uid})`"
          d="M2.3,540.3C3.3,491.4-0.3,434,5.1,386
          c5.5-49,51.9-76.5,98.3-75c42.2,0.8,87.9-1.6,129.7-0.3c78.2,2.4,88.7,5.9,87.6-74.3l-0.5-95c-0.1-22.6-1.4-46.2,2.1-68.6
          c7.1-43.3,45.8-71.1,88.3-71.9c85-1.6,171,1.5,255.9-0.3C783.2-2,778.2,85.3,774.3,171.3c-0.5,29.9-1,60.8-0.9,90.6
          c0.1,29.1,8.5,47.7,40,49.3c16.3,0.8,32.8,0.2,48.9,0.2l105.5-0.3c17.9,0,39.2-1.2,56.5,2.7c42.7,9.8,69,45.5,68.6,88.8
          c3.4,25.6,1,105.4,1.1,134.9c-21.2,0-53.9,1.1-74.1-0.2c-50.5,0.3-116.5,4.3-164.7-0.1c-32.1-3-49.7-154.3-113.5-114.6
          c-18,10.9-23.4,29.8-28.9,48.8c-20.6,71.5-46.1,143.1-63.7,215.4c-2.6-35.5-10.9-76.1-16.8-112.1C611.2,445.2,590,314.3,563.2,186
          c-5-23.7-49.7-35-61.8-6.7c-25,58.6-38.7,123-59.1,183.5c-26.5,80.7-56.3,160.6-82.1,241.5c-53.7-81-85.9-65.4-173.5-65.1L2.3,540.3
          z"
        />
      </g>

      <!-- ЛУЧ СВЕТА, БЕГУЩИЙ СТРОГО ВНУТРИ ЩЕЛИ КАРДИОГРАММЫ -->
      <!-- Внешний неоновый ореол луча -->
      <path
        class="ecg-pulse-beam-glow"
        d="M 0,571.5 L 220,571.5 Q 300,571.5 357,640 L 445,450 L 535,234 L 640,735 L 745,463 L 830,569 L 1094.4,569"
        fill="none"
        stroke="#2BE6ED"
        stroke-width="26"
        stroke-linecap="round"
        stroke-linejoin="round"
        pathLength="1000"
        :filter="`url(#ecg-beam-glow-${uid})`"
      />

      <!-- Центральное яркое ядро луча -->
      <path
        class="ecg-pulse-beam-core"
        d="M 0,571.5 L 220,571.5 Q 300,571.5 357,640 L 445,450 L 535,234 L 640,735 L 745,463 L 830,569 L 1094.4,569"
        fill="none"
        stroke="#ffffff"
        stroke-width="12"
        stroke-linecap="round"
        stroke-linejoin="round"
        pathLength="1000"
      />
    </svg>
  </div>
</template>

<style scoped>
.animated-logo-loader {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  user-select: none;
  pointer-events: none;
}

.animated-logo-svg {
  width: 100%;
  height: 100%;
  overflow: visible;
  transform-origin: center center;
  animation: logo-heartbeat 1.55s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  will-change: transform;
}

/* Чистая пульсация сердца (приближение/отдаление) без фоновых теней/ореолов */
@keyframes logo-heartbeat {
  0% {
    transform: scale(1);
  }
  22% {
    /* Главный толчок приближения */
    transform: scale(1.05);
  }
  38% {
    transform: scale(1.015);
  }
  50% {
    /* Вторичный мягкий эхо-вздох */
    transform: scale(1.03);
  }
  68% {
    transform: scale(0.995);
  }
  88%, 100% {
    transform: scale(1);
  }
}

/* Неоновый ореол бегущего луча */
.ecg-pulse-beam-glow {
  stroke-dasharray: 240 1000;
  animation: ecg-beam-travel 1.55s cubic-bezier(0.3, 0, 0.2, 1) infinite;
  will-change: stroke-dashoffset, opacity;
  opacity: 0.95;
}

/* Яркое световое ядро луча */
.ecg-pulse-beam-core {
  stroke-dasharray: 200 1000;
  animation: ecg-beam-travel 1.55s cubic-bezier(0.3, 0, 0.2, 1) infinite;
  will-change: stroke-dashoffset, opacity;
}

/* Плавный непрерывный проход луча строго по прорези кардиограммы слева направо */
@keyframes ecg-beam-travel {
  0% {
    stroke-dashoffset: 240;
    opacity: 0;
  }
  4% {
    opacity: 1;
  }
  74% {
    stroke-dashoffset: -1000;
    opacity: 1;
  }
  84%, 100% {
    stroke-dashoffset: -1000;
    opacity: 0;
  }
}

/* Доступность: статичный вид при отключенной анимации */
@media (prefers-reduced-motion: reduce) {
  .animated-logo-svg {
    animation: none;
    transform: none;
  }
  .ecg-pulse-beam-glow,
  .ecg-pulse-beam-core {
    animation: none;
    opacity: 0;
  }
}
</style>
